import { DESKTOP_DEVICE_SETTING_DEPENDENCIES } from "Constants";
import { OptionType } from "elements";
import { useFormikContext } from "formik";
import { Capabilities, PolicyDeploymentScope } from "generated/graphql.schema";
import _ from "lodash";
import { useQuery } from "urql";
import { useTranslation } from "utilities/i18n";
import {
  AvailablePolicyCapabilitiesQuery,
  ResolvedCapabilities,
  CapabilitiesQuery
} from "generated/graphql";
import { MySettingsProps } from "../Settings/MySettings";
import {
  GroupInfo,
  SiteInfo
} from "../Settings/ProvisionedDeviceSettingsRouter";
import { MyDesktopDeviceSettingsProps } from "./MyDesktopDeviceSettings";
import {
  DesktopDevicePolicyScope,
  DevicePolicyInfo,
  HardwareModelProps,
  UserGroupInfo
} from "./ProvisionedDesktopDeviceSettingsRouter";
import { desktopDeviceSettingsSchemaFields } from "./schemas/desktopDeviceSettingsSchema";
import {
  BasicDesktopDeviceSettings,
  BooleanSettingProps,
  C10Data,
  StringSettingProps
} from "./schemas/types";
import { capabilityCategories } from "./Shared/DesktopDeviceCategories";
import { generateC10String, getC10Value } from "./utilities/c10";
import { booleanFromString, getStringScopeRule } from "./utilities/utils";
import { useContext } from "react";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

export type SettingProps = {
  "data-testid": string;
  id: string;
  label: string;
  name: string;
};

const MUTE_OFF_ALERTS = "mute_off_alert";

export const useGetDesktopDeviceSettingData = (
  accessor: string,
  options: OptionType[],
  type: string
) => {
  const { values: desktopDeviceValues } = useFormikContext<
    BasicDesktopDeviceSettings
  >();
  const desktopDeviceSettings = desktopDeviceSettingsSchemaFields;
  const values: StringSettingProps | BooleanSettingProps = getC10Value(
    desktopDeviceValues,
    generateC10String(accessor, false).split(".")
  );
  const settings: SettingProps = _.get(
    desktopDeviceSettings,
    generateC10String(accessor)
  );
  const autoEnabledSliders = [
    "video.white_balance",
    "video.exposure",
    "video.focus"
  ];
  const showAutoEnableButton = autoEnabledSliders.includes(accessor);
  let hideDependency = false;
  let disableDependency = false;
  DESKTOP_DEVICE_SETTING_DEPENDENCIES.forEach(dependency => {
    if (dependency.accessor === accessor) {
      dependency.when.forEach(source => {
        const dependencyValue = getC10Value(
          desktopDeviceValues,
          generateC10String(source.accessor, false).split(".")
        );
        if (dependencyValue && source.value === dependencyValue.value) {
          switch (dependency.action) {
            case "disable":
              disableDependency = true;
              break;
            case "hide":
              hideDependency = true;
              break;
            default:
              break;
          }
        }
      });
    }
  });
  let dropdownOptions: OptionType[] = [];
  if (values === undefined) {
    return {
      values,
      settings,
      desktopDeviceSettings,
      dropdownOptions,
      showAutoEnableButton,
      disableDependency,
      hideDependency
    };
  }

  if (type === "dropdown" && values.value !== undefined) {
    if (options.length > 0) {
      dropdownOptions = options.filter(option =>
        values.constraints.options.includes(option.value)
      );
    } else {
      // set options from the constraints if there are no options passed in
      dropdownOptions = values.constraints?.options?.map(constraint => ({
        value: constraint,
        text: constraint
      }));
    }
  }
  return {
    values,
    settings,
    desktopDeviceSettings,
    dropdownOptions,
    showAutoEnableButton,
    disableDependency,
    hideDependency
  };
};

export const useGetInitialValues = (
  obj: C10Data | Capabilities | undefined
): BasicDesktopDeviceSettings | null => {
  if (!obj) return null;
  const values = _.cloneDeep(obj);
  let objHasValues;
  const cleanUpObj = (obj: Record<any, any>) => {
    if (obj.hasOwnProperty("value")) {
      return true;
    }

    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        const leafNode = cleanUpObj(obj[key]);
        if (leafNode && obj[key] !== null) {
          objHasValues = true;
          let value = obj[key]["value"];
          let constraints = obj[key]["constraints"];
          value = booleanFromString(value);
          if (key === MUTE_OFF_ALERTS) {
            value = obj[key]["value"];
          }
          if (constraints) {
            const [min, max, step] = constraints["options"];
            // convert slider value to checkbox value due to constraint range
            if (min === "0" && max === "1" && step === "1") {
              value = value === "1";
            }
          }
          obj[key]["value"] = value;
        }
      }
      if (obj[key] === null) {
        delete obj[key];
      }
    });
    return false;
  };
  cleanUpObj(values);
  return objHasValues ? (values as BasicDesktopDeviceSettings) : null;
};

export const useInheritanceAndIdentifier = (
  deploymentScope: PolicyDeploymentScope | DesktopDevicePolicyScope | null,
  props: MySettingsProps | MyDesktopDeviceSettingsProps
) => {
  const { t } = useTranslation();
  let policyIdentifier = "";
  let inheritedBy = "";

  switch (deploymentScope) {
    case PolicyDeploymentScope.DeviceModel:
    case DesktopDevicePolicyScope.Model:
      policyIdentifier = props.deviceModel;
      inheritedBy = t("MY_SETTINGS_INHERITANCE_MODEL", {
        model: props.deviceModel
      });
      break;
    case PolicyDeploymentScope.Site:
    case DesktopDevicePolicyScope.Site:
      policyIdentifier = props.site ?? "";
      inheritedBy = t("MY_SETTINGS_INHERITANCE_SITE", {
        model: props.deviceModel,
        site: props.site
      });
      break;
    case PolicyDeploymentScope.Group:
    case DesktopDevicePolicyScope.Group:
      policyIdentifier = props.group ?? "";
      inheritedBy = t("MY_SETTINGS_INHERITANCE_GROUP", {
        model: props.deviceModel,
        group: props.group
      });
      break;
    case DesktopDevicePolicyScope.User_Group:
      policyIdentifier = props.userGroup ?? "";
      inheritedBy = t("MY_SETTINGS_INHERITANCE_USER_GROUP", {
        model: props.deviceModel,
        userGroup: props.userGroup
      });
      break;
    case PolicyDeploymentScope.Device:
    case DesktopDevicePolicyScope.Device:
      policyIdentifier = props.device?.name ?? "";
      inheritedBy = t("MY_SETTINGS_INHERITANCE_DEVICE", {
        model: props.deviceModel
      });
      break;
  }

  return { policyIdentifier, inheritedBy };
};

export const useGroupPolicyQuery = (
  isC10ListEmpty?: boolean,
  policyCapabilities?: AvailablePolicyCapabilitiesQuery,
  group?: GroupInfo,
  site?: SiteInfo,
  userGroup?: UserGroupInfo,
  hardwareModel?: HardwareModelProps,
  fetchingPolicyCapabilities?: boolean
) => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const query: Partial<C10Data> = {};
  const capabiltyValue = `{value, constraints{options}}`;

  if (!isC10ListEmpty) {
    policyCapabilities?.capabilitiesSuperset.forEach(capability => {
      createC10QueryObject(capability.id.split("."), query, capabiltyValue);
    });
  }

  const queryString = JSON.stringify(query);

  const noSettingsToDisplay =
    Object.keys(query).length === 0 ||
    Object.keys(query?.com?.poly || {}).length === 0;

  const graphqlQueryString = noSettingsToDisplay
    ? `{com{poly{audio{bass${capabiltyValue}}}}}`
    : queryString.replace(/['":]+/g, "");

  const scopeRule = getStringScopeRule(group, site, userGroup, hardwareModel);

  const groupCapabilitiesQuery = `
    query GroupPolicyCapabilities {
      policyCapabilities(
        rules: {
          and: [
            {
              equal: {
                key: "tenantId"
                value: "${tenantId}"
              }
            }
            {
              equal: {
                key: "modelId"
                value: "${hardwareModel?.id}"
              }
            }
            ${scopeRule}
          ]
      }) {
        capabilities ${graphqlQueryString}
        sources {
          id
          collectionRule {
            value
          }
          name
          updatedAt
          priority
          type
          capabilities ${graphqlQueryString}
        }
      }
    }
  `;

  const [
    {
      data: groupPolicy,
      fetching: fetchingGroupPolicy,
      error: errorFetchingGroupPolicy
    }
  ] = useQuery<{ policyCapabilities: ResolvedCapabilities }, object>({
    query: groupCapabilitiesQuery,
    pause: fetchingPolicyCapabilities || noSettingsToDisplay,
    requestPolicy: "network-only"
  });

  return {
    groupPolicy,
    fetchingGroupPolicy,
    errorFetchingGroupPolicy,
    noSettingsToDisplay
  };
};

export const useDeviceGroupPolicyQuery = (
  isC10ListEmpty?: boolean,
  capabilities?: CapabilitiesQuery["capabilities"],
  device?: DevicePolicyInfo,
  fetchingPolicyCapabilities?: boolean
) => {
  const query: Partial<C10Data> = {};
  const capabiltyValue = `{value, constraints{options}}`;

  if (!isC10ListEmpty && capabilities) {
    capabilities.forEach(capability => {
      createC10QueryObject(capability.id.split("."), query, capabiltyValue);
    });
  }

  const queryString = JSON.stringify(query);
  const noSettingsToDisplay =
    Object.keys(query).length === 0 ||
    Object.keys(query?.com?.poly || {}).length === 0;

  const graphqlQueryString = noSettingsToDisplay
    ? `{com{poly{audio{bass${capabiltyValue}}}}}`
    : queryString.replace(/['":]+/g, "");

  const devicePolicyCapabilitiesQuery = `
    query DevicePolicyCapabilities {
      devicePolicyCapabilities(
        productId: "${device?.productId}"
        deviceId: "${device?.id}"
        ) {
        deviceId
        capabilities ${graphqlQueryString}
        sources {
          id
          name
          updatedAt
          priority
          type
          collectionRule {
            value
          }
          capabilities ${graphqlQueryString}
        }
      }
    }
  `;

  const [
    {
      data: devicePolicy,
      fetching: fetchingDevicePolicy,
      error: errorFetchingDevicePolicy
    },
    executeQuery
  ] = useQuery<{ devicePolicyCapabilities: ResolvedCapabilities }, object>({
    query: devicePolicyCapabilitiesQuery,
    pause: !device?.id || fetchingPolicyCapabilities
  });
  return {
    devicePolicy,
    fetchingDevicePolicy,
    errorFetchingDevicePolicy,
    noSettingsToDisplay,
    executeQuery
  };
};

// used to create c10 query object from given capabilities superset array
/*
  Ex:
  [
    {
      "id": "com.poly.audio.treble"
    }
  ]

  com: {
    poly: {
      audio: {
        treble :{
          value,
          constraints: {
            options
          }
        }
      }
    }
  }
*/
export const createC10QueryObject = (
  path: string[],
  obj: Record<any, any>,
  value: string
) => {
  let key = path[0];
  if (path.length === 1) {
    obj[key] = value;
  } else {
    // we only want to include c10 keys that are relavant to form settings
    if (capabilityCategories.includes(key)) {
      if (!obj[key]) {
        obj[key] = {};
      }
      createC10QueryObject(path.slice(1), obj[key], value);
    }
  }
};
